import {isFeatureEnabled} from 'feature-flags';
import {
  Button,
  ButtonGroup,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Separator,
} from 'platform/components';
import {Box, Hide, Show, Text, VStack} from 'platform/foundation';
import {boolean, object} from 'yup';

import {useParams} from 'react-router-dom';

import {
  GetBackgroundRemovalResponseBody,
  useCreateBackgroundRemovalMutation,
  useUpdateBackgroundRemovalV2Mutation,
} from '@dms/api/sales';
import {useUploadLicensePlateRemovalFileMutation} from '@dms/api/settings';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {
  handleApiError,
  ORIGINAL_BACKGROUND_REMOVAL_IDS,
  MERCEDES_BACKGROUND_REMOVAL_IDS,
  MERCEDES_BACKGROUND_ZIZAK_REMOVAL_IDS,
  DISABLE_LICENSE_PLATE_REMOVAL_BACKGROUND_IDS,
  MERCEDES_BACKGROUND_VSP_REMOVAL_IDS,
  SWAUTOMOBILY_BACKGROUND_REMOVAL_IDS,
  getBase64,
  DAVOCAR_BACKGROUND_REMOVAL_IDS,
  VSP_BACKGROUND_REMOVAL_IDS,
} from '@dms/shared';

import {useNavigate, yupString, ApiError} from 'shared/web';

import {BackgroundRemovalFormValues} from '../types/BackgroundRemovalFormValues';
import {BackgroundSelector} from './BackgroundSelector';

type BackgroundRemovalFormType = {
  settings?: GetBackgroundRemovalResponseBody;
};

export function BackgroundRemovalForm(props: BackgroundRemovalFormType) {
  const {id} = useParams();
  const navigate = useNavigate();

  const backgroundIds = [
    ...ORIGINAL_BACKGROUND_REMOVAL_IDS,
    ...(isFeatureEnabled(featureFlags.MERCEDES_BACKGROUND_PHOTOS)
      ? MERCEDES_BACKGROUND_REMOVAL_IDS
      : []),
    ...(isFeatureEnabled(featureFlags.MERCEDES_BACKGROUND_ZIZAK)
      ? MERCEDES_BACKGROUND_ZIZAK_REMOVAL_IDS
      : []),
    ...(isFeatureEnabled(featureFlags.MERCEDES_BACKGROUND_VSP)
      ? MERCEDES_BACKGROUND_VSP_REMOVAL_IDS
      : []),
    ...(isFeatureEnabled(featureFlags.MERCEDES_BACKGROUND_SW)
      ? SWAUTOMOBILY_BACKGROUND_REMOVAL_IDS
      : []),
    ...(isFeatureEnabled(featureFlags.DAVOCAR_BACKGROUND) ? DAVOCAR_BACKGROUND_REMOVAL_IDS : []),
    ...(isFeatureEnabled(featureFlags.VSP_BACKGROUND) ? VSP_BACKGROUND_REMOVAL_IDS : []),
  ];

  const [createConfiguration] = useCreateBackgroundRemovalMutation();
  const [updateConfiguration] = useUpdateBackgroundRemovalV2Mutation();
  const [uploadLicensePlateRemovalFile] = useUploadLicensePlateRemovalFileMutation();

  const onSubmit: FormSubmitHandler<BackgroundRemovalFormValues> = async (
    values,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    const isCustomLicensePlateEnabled = values.registrationPlateRemovalEnabled;
    const shouldUploadNewCustomLicensePlate =
      isCustomLicensePlateEnabled &&
      values.file?.id &&
      values.file.id !== props.settings?.registrationPlateRemovalFileId;

    let registrationPlateRemovalFileId: string | undefined;

    if (shouldUploadNewCustomLicensePlate) {
      try {
        const uploadResult = await uploadLicensePlateRemovalFile({
          uploadLicensePlateRemovalFileRequestBody: {
            filename: values.file!.id,
            fileContent: values.file!.url,
          },
        }).unwrap();
        registrationPlateRemovalFileId = uploadResult?.id;
      } catch (error) {
        handleApiError(error as ApiError);
        return;
      }
    } else {
      registrationPlateRemovalFileId = values.file?.id;
    }

    const args = {
      name: values.name,
      backgroundId: values.backgroundId,
      registrationPlateRemovalEnabled: values.registrationPlateRemovalEnabled,
      windowTintingEnabled: false, // See T20-63137
      registrationPlateRemovalFileId: isCustomLicensePlateEnabled
        ? (registrationPlateRemovalFileId ?? null)
        : null,
    };
    if (id) {
      await updateConfiguration({settingId: id, updateBackgroundRemovalRequestBody: args})
        .unwrap()
        .then(() => {
          if (blockerContext?.wasSubmittedFromBlocker) {
            return;
          }
          navigate(settingsRoutes.backgroundRemoval);
        })
        .catch((e: ApiError) =>
          handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
        );
    } else {
      await createConfiguration({createBackgroundRemovalRequestBody: args})
        .unwrap()
        .then(() => {
          if (blockerContext?.wasSubmittedFromBlocker) {
            return;
          }
          navigate(settingsRoutes.backgroundRemoval);
        })
        .catch((e: ApiError) =>
          handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
        );
    }
  };

  return (
    <Form<BackgroundRemovalFormValues>
      onSubmit={onSubmit}
      schema={schema}
      shouldWatchForUnsavedChanges
      defaultValues={{
        name: props.settings?.name ?? '',
        backgroundId: props.settings?.backgroundId ?? '49716',
        backgroundReplacementEnabled: props.settings?.backgroundReplacementEnabled ?? true,
        registrationPlateRemovalEnabled: props.settings?.registrationPlateRemovalEnabled ?? false,
        windowTintingEnabled: props.settings?.windowTintingEnabled ?? false,
        file:
          props.settings?.registrationPlateRemovalFileId &&
          props.settings.registrationPlateRemovalFileLink
            ? {
                id: props.settings.registrationPlateRemovalFileId,
                url: props.settings.registrationPlateRemovalFileLink,
              }
            : null,
      }}
    >
      {(control, formApi, navigateWithoutBlocker) => {
        const [currentBgId, isRegistrationPlateRemovalEnabled] = formApi.watch([
          'backgroundId',
          'registrationPlateRemovalEnabled',
        ]);
        const isLicensePlateRemovalDisabled =
          DISABLE_LICENSE_PLATE_REMOVAL_BACKGROUND_IDS.includes(currentBgId);

        return (
          <VStack spacing={4}>
            <FormField
              label={i18n.t('page.salesSettings.backgroundRemoval.configurationName')}
              control={control}
              name="name"
              type="text"
              isRequired
              data-testid="backgroundRemoval-configurationName"
            />

            <Separator spacing={2} />

            <FormField
              label={i18n.t('page.salesSettings.backgroundRemoval.labels.replaceBackground')}
              control={control}
              name="backgroundReplacementEnabled"
              type="switch"
              isDisabled
              data-testid="backgroundRemoval-backgroundReplacementEnabled"
            />
            <Box paddingLeft={12}>
              <VStack spacing={4}>
                <Text size="small" color="tertiary">
                  {i18n.t('page.salesSettings.backgroundRemoval.description.replaceBackground')}
                </Text>
                <BackgroundSelector
                  name="backgroundId"
                  options={backgroundIds}
                  control={control}
                  formApi={formApi}
                  externalOnClick={(selectedBgId) => {
                    if (DISABLE_LICENSE_PLATE_REMOVAL_BACKGROUND_IDS.includes(selectedBgId)) {
                      formApi.setValue('registrationPlateRemovalEnabled', false);
                    }
                  }}
                />
              </VStack>
            </Box>

            <Separator spacing={2} />

            <FormField
              label={i18n.t('page.salesSettings.backgroundRemoval.labels.replacePlate')}
              control={control}
              name="registrationPlateRemovalEnabled"
              type="switch"
              data-testid="backgroundRemoval-registrationPlateRemovalEnabled"
              isDisabled={isLicensePlateRemovalDisabled}
            />
            <Box paddingLeft={12}>
              <VStack spacing={4} justify="flex-start" align="flex-start">
                <Hide whenFeatureEnabled={featureFlags.SALES_BR_CUSTOM_LICENSE_PLATE}>
                  <Text size="small" color="tertiary">
                    {i18n.t('page.salesSettings.backgroundRemoval.description.replacePlate')}
                  </Text>
                </Hide>
                <Show whenFeatureEnabled={featureFlags.SALES_BR_CUSTOM_LICENSE_PLATE}>
                  <Text size="small" color="tertiary">
                    {i18n.t('page.salesSettings.backgroundRemoval.customLicensePlate.description')}
                  </Text>
                </Show>
                <Show whenFeatureEnabled={featureFlags.SALES_BR_CUSTOM_LICENSE_PLATE}>
                  <Box width={50} height="auto">
                    <FormField
                      control={control}
                      type="uploadImage"
                      name="file"
                      previewWidth="auto"
                      previewHeight="auto"
                      uploadErrorIcon="file/upload"
                      isDisabled={!isRegistrationPlateRemovalEnabled}
                      uploadFileRequest={(file) =>
                        new Promise(async (resolve, reject) => {
                          const img = new Image();
                          const base64 = await getBase64(file);
                          const cleanup = () => {
                            img.onload = null;
                            img.onerror = null;
                          };
                          img.src = typeof base64 === 'string' ? base64 : '';
                          img.onload = () => {
                            cleanup();
                            const aspectRatio = img.width / img.height;
                            const EPSILON = 0.01;
                            const MAX_SIZE_MB = 1;
                            const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
                            if (file.size > MAX_SIZE_BYTES) {
                              formApi.setError('file', {
                                message: i18n.t(
                                  'page.salesSettings.backgroundRemoval.customLicensePlate.errorMessageSize'
                                ),
                              });
                              return reject('File size exceeds 1MB');
                            }
                            if (Math.abs(aspectRatio - 5) > EPSILON) {
                              formApi.setError('file', {
                                message: i18n.t(
                                  'page.salesSettings.backgroundRemoval.customLicensePlate.errorMessage'
                                ),
                              });
                              return reject('Incorrect image ratio');
                            } else {
                              formApi.clearErrors('file');
                              resolve({
                                id: file.name,
                                url: typeof base64 === 'string' ? base64 : '',
                              });
                            }
                          };
                          img.onerror = () => {
                            cleanup();
                            reject(new Error('Something went wrong with upload'));
                          };
                        })
                      }
                    />
                  </Box>
                </Show>
              </VStack>
            </Box>

            <Separator spacing={2} />

            {/* Hidden option - see T20-63137
            <FormField
              label={i18n.t('page.salesSettings.backgroundRemoval.labels.tintWindows')}
              control={control}
              name="windowTintingEnabled"
              type="switch"
              isDisabled={!props.settings?.windowTintingEnabled}
              data-testid="backgroundRemoval-windowTintingEnabled"
            />
            <Box paddingLeft={12}>
              <Text size="small" color="tertiary">
                {i18n.t('page.salesSettings.backgroundRemoval.description.tintWindows')}
              </Text>
            </Box>

            <Separator spacing={2} />
            */}

            <ButtonGroup align="right">
              <Button
                title={i18n.t('general.actions.discardChanges')}
                variant="secondary"
                onClick={() => navigateWithoutBlocker(settingsRoutes.backgroundRemoval)}
                data-testid="backgroundRemoval-discardChangesBtn"
              />
              <FormButton
                title={i18n.t('general.actions.saveAndClose')}
                control={control}
                type="submit"
                data-testid="backgroundRemoval-submitBtn"
              />
            </ButtonGroup>
          </VStack>
        );
      }}
    </Form>
  );
}

const schema = object({
  backgroundReplacementEnabled: boolean().defined().isTrue(),
  backgroundId: yupString,
  registrationPlateRemovalEnabled: boolean().defined(),
  //windowTintingEnabled: boolean().defined(),
  name: yupString.required(),
  file: object({
    id: yupString,
    url: yupString,
  }).nullable(),
});
